import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  CLIENT_BILLING_LABEL,
  CLIENT_BILLING_STATUSES,
  PAYOUT_STATUS_LABEL,
  PAYOUT_STATUSES,
  type ClientBillingStatus,
  type PaymentSheet,
  type PaymentSheetPlayer,
  type PayoutStatus,
} from '@workflow/contracts'
import { ActionMenu, Avatar, AvatarStack, Button, cn, EASE_OUT, IconButton, Icons, Loading, StateNote, StatusMark, Truncate } from '@workflow/ui'
import { navigate } from '@/app/router'
import { describeError } from '@/lib/api/client'
import {
  useArchivePaymentSheet,
  usePaymentSheet,
  useRefreshPaymentSheet,
  useResendStatement,
  useSetSheetBilling,
  useSetSheetRate,
  useSharePaymentSheet,
  useUpdateSheetPlayer,
} from '@/lib/api/hooks'
import { formatCreated } from '@/lib/format'
import { PageFrame } from '@/shell/PageFrame'
import { SheetGrid, type Align, type Cell, type Column, type Row } from './SheetGrid'
import { CellMenu } from './CellMenu'
import { amountText, dayLabel, daysOf, downloadCsv, hoursText, money, PAYOUT_HUE, periodLabel, rateWords } from './sheetMath'

const MONEY_INPUT = /^\d+(?:\.\d{1,2})?$/

/**
 * A payment sheet, drawn the way the studio's Google Sheet was: lettered
 * columns, numbered rows, gridlines, a formula bar that reads the selected
 * cell, a dropdown in the status cells, and one tab per client at the foot.
 * Hours come from the jobs; the extra, its note, the method, the account,
 * a column's rate and the statuses are typed in the grid.
 */
export function PaymentSheetPage({ id }: { id: string }) {
  const sheet = usePaymentSheet(id)
  if (sheet.isLoading) return <Loading label="Loading the payment sheet" />
  if (sheet.error || !sheet.data) {
    return (
      <PageFrame title="Payment sheet">
        <StateNote title="The sheet did not load." detail={describeError(sheet.error)} />
      </PageFrame>
    )
  }
  return <SheetView sheet={sheet.data} />
}

function SheetView({ sheet }: { sheet: PaymentSheet }) {
  const [full, setFull] = useState(false)
  const refresh = useRefreshPaymentSheet()
  const share = useSharePaymentSheet()
  const archive = useArchivePaymentSheet(() => navigate({ name: 'payments' }))
  const everyone = useSheetGrid(sheet, null)

  return (
    <PageFrame
      title={sheet.title}
      measure={880}
      description={
        // The period on one line, the sharing on the next: neither wraps inside a date.
        <>
          <span className="block whitespace-nowrap">
            {periodLabel(sheet.periodStart, sheet.periodEnd)} · {sheet.players.length} {sheet.players.length === 1 ? 'player' : 'players'}
          </span>
          <span className="block whitespace-nowrap">
            {sheet.sharedAt ? `Shared with players ${formatCreated(sheet.sharedAt)}` : 'Not shared with players yet'}
          </span>
        </>
      }
      actions={
        <>
          <Button size="sm" icon={<Icons.spreadsheet />} onClick={() => setFull(true)}>Sheet view</Button>
          <IconButton label="Refresh from jobs" size="sm" disabled={refresh.isPending} onClick={() => refresh.mutate(sheet.id)}>
            <Icons.retry />
          </IconButton>
          <IconButton label="Download CSV" size="sm" onClick={() => downloadCsv(sheet.title, everyone.csv)}>
            <Icons.download />
          </IconButton>
          {sheet.sharedAt === null ? (
            <Button size="sm" variant="primary" icon={<Icons.shared />} busy={share.isPending} disabled={sheet.players.length === 0} onClick={() => share.mutate(sheet.id)}>
              Share with players
            </Button>
          ) : null}
          <ActionMenu
            label="Sheet actions"
            sections={[{
              items: [{
                key: 'archive', label: 'Archive sheet', icon: <Icons.archive size="sm" />, danger: true,
                ask: {
                  title: `Archive ${sheet.title}?`,
                  detail: 'The sheet leaves the list and its hours become free for another sheet. Players stop seeing their statement.',
                  confirmLabel: 'Archive',
                },
                onSelect: () => archive.mutate(sheet.id),
              }],
            }]}
          />
        </>
      }
    >
      <SheetNotes sheet={sheet} />
      <SimpleTable sheet={sheet} />
      <AnimatePresence>{full ? <FullSheet sheet={sheet} onClose={() => setFull(false)} /> : null}</AnimatePresence>
    </PageFrame>
  )
}

/** What needs the studio's eye before paying: jobs without a rate, and who has reviewed. */
function SheetNotes({ sheet }: { sheet: PaymentSheet }) {
  const unrated = sheet.tasks.filter((task) => task.rateAmount === null)
  const accepted = sheet.players.filter((player) => player.review === 'accepted')
  const disputed = sheet.players.filter((player) => player.review === 'disputed')
  const waiting = sheet.players.length - accepted.length - disputed.length
  return (
    <>
      {unrated.length > 0 ? (
        <div className="mb-3 flex items-start gap-2.5 rounded-xl bg-status-amber/10 px-4 py-2.5 text-caption text-foreground">
          <span className="mt-0.5 text-status-amber"><Icons.error size="sm" /></span>
          <span>
            {unrated.map((task) => task.jobTitle).join(', ')} {unrated.length === 1 ? 'has' : 'have'} no player rate. Those hours pay nothing,
            and nobody with hours there can be marked paid, until a rate is typed into the rate cell under the job's name in the sheet view.
          </span>
        </div>
      ) : null}
      {sheet.sharedAt !== null && sheet.players.length > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-caption text-muted-foreground">
          {accepted.length > 0 ? (
            <span className="inline-flex items-center gap-2">
              <span className="text-status-green"><Icons.check size="sm" /></span>
              Accepted
              <AvatarStack size="xs" people={accepted.map((player) => ({ key: player.playerId, name: player.name, src: player.pictureUrl }))} />
            </span>
          ) : null}
          {disputed.length > 0 ? (
            <span className="inline-flex items-center gap-2">
              <span className="text-status-red"><Icons.error size="sm" /></span>
              Disputed
              <AvatarStack size="xs" people={disputed.map((player) => ({ key: player.playerId, name: player.name, src: player.pictureUrl }))} />
            </span>
          ) : null}
          {waiting > 0 ? (
            <span>
              {waiting === sheet.players.length
                ? 'No player has reviewed their statement yet'
                : `${waiting} ${waiting === 1 ? 'player has' : 'players have'} not reviewed their statement yet`}
            </span>
          ) : null}
        </div>
      ) : null}
    </>
  )
}

/**
 * The sheet view: the whole window given to the spreadsheet, opened over the
 * page in the same tab and closed back to the simple table with Escape.
 */
function FullSheet({ sheet, onClose }: { sheet: PaymentSheet; onClose: () => void }) {
  const [tab, setTab] = useState<string>('all')
  const client = sheet.clients.find((entry) => entry.key === tab)
  const grid = useSheetGrid(sheet, client?.key ?? null)

  useEffect(() => {
    const close = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Escape first leaves an edit or an open dropdown; only a bare Escape closes the sheet.
      const target = event.target as HTMLElement | null
      if (target?.tagName === 'INPUT' || document.querySelector('[role="listbox"]')) return
      onClose()
    }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [onClose])

  // Drawn at the window's level: the page panel moves, and a fixed layer inside it would be held to its box.
  return createPortal(
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={`${sheet.title} sheet`}
      // Opening rises out of the page and settles; closing sinks back the same way, a little quicker.
      initial={{ opacity: 0, scale: 0.94, y: 24, filter: 'blur(6px)' }}
      animate={{ opacity: 1, scale: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.5, ease: EASE_OUT } }}
      exit={{ opacity: 0, scale: 0.96, y: 16, filter: 'blur(4px)', transition: { duration: 0.32, ease: [0.4, 0, 0.2, 1] } }}
      className="fixed inset-0 z-60 flex flex-col overflow-hidden bg-page"
    >
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-hairline px-4 sm:px-6">
        <IconButton label="Back to the simple view" size="sm" onClick={onClose} tooltipSide="bottom"><Icons.back /></IconButton>
        <div className="min-w-0 flex-1">
          <p className="truncate text-ui font-medium text-foreground">{sheet.title}</p>
          <p className="truncate text-caption text-muted-foreground">{periodLabel(sheet.periodStart, sheet.periodEnd)}</p>
        </div>
        <IconButton label="Download CSV" size="sm" tooltipSide="bottom" onClick={() => downloadCsv(`${sheet.title}${client ? ` - ${client.name}` : ''}`, grid.csv)}>
          <Icons.download />
        </IconButton>
        <IconButton label="Close the sheet" size="sm" tooltipSide="bottom" onClick={onClose}><Icons.close /></IconButton>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-6">
        <SheetNotes sheet={sheet} />
        {client ? <BillingBar sheet={sheet} clientKey={client.key} status={client.status} /> : null}
        <SheetGrid editableGrid grid={grid} emptyMessage={sheet.players.length === 0 ? 'No unpaid hours were logged on or before the end of this period. Refresh once players log their work.' : undefined} />
        <div className="flex items-center gap-0.5 overflow-x-auto rounded-b-xl border border-t-0 border-hairline bg-surface-sunken px-1" role="tablist" aria-label="Sheet tabs">
          {[{ key: 'all', name: 'All Clients' }, ...sheet.clients].map((entry) => (
            <button
              key={entry.key || 'internal'}
              type="button"
              role="tab"
              aria-selected={tab === entry.key}
              onClick={() => setTab(entry.key)}
              className={cn(
                'h-9 shrink-0 px-4 text-caption whitespace-nowrap outline-none transition-colors duration-fast focus-visible:ring-1 focus-visible:ring-foreground',
                tab === entry.key
                  ? 'bg-surface font-medium text-foreground shadow-[inset_0_-2px_0_var(--color-primary)]'
                  : 'text-muted-foreground hover:bg-tint hover:text-foreground',
              )}
            >
              {entry.name}
            </button>
          ))}
        </div>
        <p className="mt-2 text-caption text-muted-foreground">
          Click a cell to read it above the grid. Double-click, or start typing, to edit the extra, its note, the method, the account, or a job's rate.
          Hours come from the jobs; open a job from its name. Escape closes the sheet.
        </p>
      </div>
    </motion.div>,
    document.body,
  )
}

/** A client's tab: what the studio bills them, and where that bill stands. */
function BillingBar({ sheet, clientKey, status }: { sheet: PaymentSheet; clientKey: string; status: ClientBillingStatus }) {
  const billing = useSetSheetBilling()
  return (
    <div className="mb-3 flex items-center gap-3 text-caption text-muted-foreground">
      Billing
      <CellMenu
        label="Billing status"
        value={status}
        options={CLIENT_BILLING_STATUSES.map((value) => ({
          value, label: CLIENT_BILLING_LABEL[value],
          mark: <StatusMark tone="idle" hue={value === 'received' ? 'green' : value === 'invoiced' ? 'blue' : undefined} />,
        }))}
        renderValue={(value) => (
          <span className="inline-flex items-center gap-1.5">
            <StatusMark tone="idle" hue={value === 'received' ? 'green' : value === 'invoiced' ? 'blue' : undefined} />
            {CLIENT_BILLING_LABEL[value]}
          </span>
        )}
        onChange={(next) => billing.mutate({ id: sheet.id, clientKey, status: next })}
        className="bg-tint text-foreground"
      />
    </div>
  )
}

// ---- The simple view -----------------------------------------------------

/** What a player is owed on this sheet: their lines plus the extra, and whether a line still lacks a rate. */
function playerTotal(sheet: PaymentSheet, player: PaymentSheetPlayer) {
  const own = sheet.lines.filter((line) => line.playerId === player.playerId)
  return {
    total: own.reduce((sum, line) => sum + (line.amount ?? 0), 0) + player.extraAmount,
    missing: own.some((line) => line.amount === null),
  }
}

/**
 * The sheet as a plain table: who gets how much, where it goes, whether they
 * agreed, and the status to move as the money goes out. The sheet view holds
 * the hours behind each total.
 */
function SimpleTable({ sheet }: { sheet: PaymentSheet }) {
  const update = useUpdateSheetPlayer()
  const resend = useResendStatement()
  if (sheet.players.length === 0) {
    return (
      <p className="py-6 text-center text-caption text-muted-foreground">
        No unpaid hours were logged on or before the end of this period. Refresh once players log their work.
      </p>
    )
  }
  const sum = sheet.players.reduce((all, player) => all + playerTotal(sheet, player).total, 0)
  const paid = sheet.players.filter((player) => player.status === 'paid')
  const paidSum = paid.reduce((all, player) => all + playerTotal(sheet, player).total, 0)
  const hours = sheet.lines.reduce((all, line) => all + line.hours, 0)
  // The same rhythm as the work on Home: rows on hairlines, no box and no header.
  // A phone stacks each row in two: name and amount, then status and review.
  const columns = 'grid grid-cols-[1.75rem_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 sm:grid-cols-[1.75rem_minmax(0,1fr)_7rem_5rem_8rem] sm:gap-x-4 sm:gap-y-0'
  const reset = 'sm:col-start-auto sm:row-start-auto'
  return (
    <ul className="flex flex-col tabular-nums">
      {sheet.players.map((player) => {
        const { total, missing } = playerTotal(sheet, player)
        return (
          <li key={player.playerId} className={cn(columns, 'border-b border-hairline py-3.5')}>
            <span className="self-start sm:self-center">
              <Avatar name={player.name} email={player.name} src={player.pictureUrl} size="sm" />
            </span>
            <span className="min-w-0">
              <Truncate text={player.name} className="text-ui font-medium text-foreground" />
              <span className="mt-0.5 block truncate text-caption text-muted-foreground" title={[player.method, player.account].filter(Boolean).join(' · ')}>
                {player.method ?? <span className="text-danger">No payment method</span>}
                {player.account ? <span className="font-mono"> · {player.account}</span> : null}
              </span>
            </span>
            <span className={cn('col-start-3 row-start-2 inline-flex items-center gap-1.5 justify-self-end text-caption sm:justify-self-start', reset)} title={reviewDetail(sheet, player)}>
              <ReviewWords sheet={sheet} player={player} />
              {sheet.sharedAt !== null && player.review === 'disputed' ? (
                <Button size="xs" variant="ghost" onClick={() => resend.mutate({ id: sheet.id, playerId: player.playerId })}>
                  Send back
                </Button>
              ) : null}
            </span>
            <span
              className={cn('col-start-3 row-start-1 inline-flex items-center justify-end gap-1 text-ui font-medium', reset, missing ? 'text-status-amber' : 'text-foreground')}
              title={missing ? 'Leaves out hours on a job with no rate; set the rate in the sheet view.' : undefined}
            >
              {missing ? <Icons.error size="xs" /> : null}
              {amountText(total)}
            </span>
            <span className={cn('col-start-2 row-start-2 justify-self-start sm:justify-self-end', reset)}>
              <PayoutMenu player={player} onChange={(status) => update.mutate({ id: sheet.id, playerId: player.playerId, input: { status } })} />
            </span>
          </li>
        )
      })}
      {/* The sheet's sum: the amount, and capsules for what it covers and how much has gone out. */}
      <li className={cn(columns, 'items-center py-4')}>
        <span className="col-start-2 row-start-1 flex min-w-0 flex-wrap items-center gap-1.5">
          <Capsule>{sheet.players.length} {sheet.players.length === 1 ? 'player' : 'players'}</Capsule>
          <Capsule>{hoursText(hours)} hours</Capsule>
        </span>
        <span className="col-start-3 row-start-1 text-right text-lg leading-tight font-semibold text-foreground">{amountText(sum)}</span>
        <span className="col-span-2 col-start-2 row-start-2 flex justify-end sm:col-start-4 sm:row-start-1">
          <Capsule>
            <span className="inline-flex items-center gap-2">
              <span>{paid.length} of {sheet.players.length} paid</span>
              <span className="border-l border-hairline pl-2">{amountText(paidSum)} paid</span>
            </span>
          </Capsule>
        </span>
      </li>
    </ul>
  )
}

/** A small capsule for one figure on the sheet's sum row. */
function Capsule({ children }: { children: ReactNode }) {
  return <span className="whitespace-nowrap rounded-full bg-tint px-2 py-0.5 text-micro text-foreground tabular-nums">{children}</span>
}

function reviewDetail(sheet: PaymentSheet, player: PaymentSheetPlayer): string {
  const when = player.reviewAt ? ` ${formatCreated(player.reviewAt)}` : ''
  if (sheet.sharedAt === null) return 'Share the sheet for players to review their statements.'
  if (player.review === 'accepted') return `${player.name} accepted their statement${when}.`
  if (player.review === 'disputed') return `${player.name} disputed${when}: “${player.dispute ?? ''}”`
  return `${player.name} has not reviewed their statement yet.`
}

function ReviewWords({ sheet, player }: { sheet: PaymentSheet; player: PaymentSheetPlayer }) {
  if (sheet.sharedAt === null) return <span className="text-muted-foreground">Not shared</span>
  if (player.review === 'accepted') {
    return <span className="inline-flex items-center gap-1.5"><span className="text-status-green"><Icons.check size="sm" /></span>Accepted</span>
  }
  if (player.review === 'disputed') return <span className="inline-flex items-center gap-1.5 text-danger"><Icons.error size="sm" />Disputed</span>
  return <span className="text-muted-foreground">Waiting</span>
}

/** A player's payout status as a dropdown chip, tinted by where the money stands. */
function PayoutMenu({ player, onChange }: { player: PaymentSheetPlayer; onChange: (status: PayoutStatus) => void }) {
  return (
    <CellMenu
      label={`Payment status for ${player.name}`}
      value={player.status}
      options={PAYOUT_STATUSES.map((value) => ({
        value, label: PAYOUT_STATUS_LABEL[value],
        mark: <StatusMark tone="idle" hue={PAYOUT_HUE[value] ?? undefined} />,
      }))}
      renderValue={(value) => (
        <span className="inline-flex items-center gap-1.5">
          <StatusMark tone="idle" hue={PAYOUT_HUE[value] ?? undefined} />
          {PAYOUT_STATUS_LABEL[value]}
        </span>
      )}
      onChange={onChange}
      className={cn('text-foreground', player.status === 'paid' ? 'bg-status-green/12' : player.status === 'on_hold' ? 'bg-status-amber/12' : player.status === 'cancelled' ? 'bg-status-red/10' : 'bg-tint')}
    />
  )
}

// ---- The grid's data -----------------------------------------------------

interface Grid {
  columns: Column[]
  rows: Row[]
  csv: (string | number)[][]
}

function useSheetGrid(sheet: PaymentSheet, clientKey: string | null): Grid {
  const update = useUpdateSheetPlayer()
  const setRate = useSetSheetRate()
  const resend = useResendStatement()
  return useMemo(() => {
    if (clientKey !== null) return clientGrid(sheet, clientKey)
    const commitPlayer = (player: PaymentSheetPlayer, field: 'extraAmount' | 'extraReason' | 'method' | 'account') => (text: string) => {
      const value = text.trim()
      if (field === 'extraAmount') {
        if (value !== '' && !MONEY_INPUT.test(value)) return
        const minor = value === '' ? 0 : Math.round(Number(value) * 100)
        if (minor !== player.extraAmount) update.mutate({ id: sheet.id, playerId: player.playerId, input: { extraAmount: minor } })
        return
      }
      if (value === (player[field] ?? '')) return
      update.mutate({ id: sheet.id, playerId: player.playerId, input: { [field]: value === '' ? null : value } })
    }
    const setStatus = (player: PaymentSheetPlayer, status: PayoutStatus) =>
      update.mutate({ id: sheet.id, playerId: player.playerId, input: { status } })

    const tasks = sheet.tasks
    const columns: Column[] = [
      { key: 'player', label: 'Player', width: 176, align: 'left' },
      ...tasks.map((task) => ({ key: `task:${task.jobId}`, label: task.jobTitle, width: 168, align: 'right' as Align })),
      { key: 'hours', label: 'Hours', width: 72, align: 'right' },
      { key: 'amount', label: 'Amount', width: 96, align: 'right' },
      { key: 'extra', label: 'Extra', width: 84, align: 'right' },
      { key: 'note', label: 'Extra note', width: 160, align: 'left' },
      { key: 'total', label: 'Total', width: 104, align: 'right' },
      { key: 'method', label: 'Method', width: 112, align: 'left' },
      { key: 'account', label: 'Account', width: 150, align: 'left' },
      { key: 'review', label: 'Review', width: 112, align: 'left' },
      { key: 'status', label: 'Status', width: 128, align: 'left' },
    ]

    // Row 1: the clients over their jobs; row 2: each job's name and rate.
    const groupCells: Record<string, Cell> = { player: { view: '', detail: '' } }
    for (const task of tasks) groupCells[`task:${task.jobId}`] = { view: task.clientName, detail: `Client: ${task.clientName}`, tone: 'muted', align: 'center' }
    const rateCells: Record<string, Cell> = { player: { view: 'Player', detail: 'Players, by the name they go by in the studio.', tone: 'strong' } }
    for (const task of tasks) {
      const rate = rateWords(task.rateType, task.rateAmount)
      rateCells[`task:${task.jobId}`] = {
        view: (
          <span className="flex flex-col text-right leading-tight">
            <Truncate text={task.jobTitle} side="bottom" className="max-w-full font-medium text-foreground" />
            <span className={cn('text-[11px]', rate ? 'text-muted-foreground' : 'text-status-amber')}>{rate || 'Set a rate'}</span>
          </span>
        ),
        detail: `${task.jobTitle} · ${task.clientName} · ${rate ? `pays ${rate}${task.rateOnSheet ? ' (set on this sheet)' : ' (the job’s rate)'}` : 'no player rate — type one to pay these hours'}`,
        tone: rate ? undefined : 'warn',
        align: 'right',
        open: () => navigate({ name: 'job', id: task.jobId }),
        edit: {
          value: task.rateAmount === null ? '' : String(task.rateAmount / 100),
          commit: (text) => {
            const value = text.trim()
            if (value !== '' && !MONEY_INPUT.test(value)) return
            const minor = value === '' ? null : Math.round(Number(value) * 100)
            if (minor === task.rateAmount || minor === 0) return
            setRate.mutate({ id: sheet.id, jobId: task.jobId, input: { rateType: task.rateType ?? 'hourly', rateAmount: minor } })
          },
        },
      }
    }
    for (const key of ['hours', 'amount', 'extra', 'note', 'total', 'method', 'account', 'review', 'status']) {
      const column = columns.find((entry) => entry.key === key)!
      groupCells[key] = { view: '', detail: '' }
      rateCells[key] = { view: column.label, detail: String(column.label), tone: 'strong', align: column.align }
    }

    const lineOf = new Map(sheet.lines.map((line) => [`${line.playerId}:${line.jobId}`, line]))
    const rows: Row[] = [
      { key: 'group', number: 1, cells: groupCells, kind: 'head' },
      { key: 'rate', number: 2, cells: rateCells, kind: 'head' },
    ]
    const csv: (string | number)[][] = [
      ['Player', ...tasks.map((task) => `${task.clientName} · ${task.jobTitle} (${rateWords(task.rateType, task.rateAmount) || 'no rate'})`),
        'Hours', 'Amount', 'Extra', 'Extra note', 'Total', 'Method', 'Account', 'Review', 'Status'],
    ]
    const totals = { hours: 0, amount: 0, extra: 0 }
    const taskHours = new Map<string, number>()

    sheet.players.forEach((player, index) => {
      const own = sheet.lines.filter((line) => line.playerId === player.playerId)
      const hours = own.reduce((sum, line) => sum + line.hours, 0)
      const amount = own.reduce((sum, line) => sum + (line.amount ?? 0), 0)
      const missing = own.filter((line) => line.amount === null)
      const total = amount + player.extraAmount
      totals.hours += hours
      totals.amount += amount
      totals.extra += player.extraAmount
      const paid = player.status === 'paid'
      const lockedWords = paid ? `${player.name} is marked paid; set them back to unpaid to change this.` : undefined
      const cells: Record<string, Cell> = {
        player: { view: <span className="font-medium text-foreground">{player.name}</span>, detail: player.name, align: 'left' },
      }
      for (const task of tasks) {
        const line = lineOf.get(`${player.playerId}:${task.jobId}`)
        if (!line) {
          cells[`task:${task.jobId}`] = { view: '', detail: `${player.name} has no hours on ${task.jobTitle}.` }
          continue
        }
        taskHours.set(task.jobId, (taskHours.get(task.jobId) ?? 0) + line.hours)
        const days = daysOf(line.entries, line.hours, sheet.periodEnd)
        const dayWords = days.map((day) => `${day.day ? dayLabel(day.day) : 'Undated'} ${hoursText(day.hours)}h`).join(', ')
        cells[`task:${task.jobId}`] = {
          view: hoursText(line.hours),
          detail: `${player.name} · ${task.jobTitle}: ${hoursText(line.hours)}h${line.amount === null ? ' · no rate yet' : ` × ${rateWords(task.rateType, task.rateAmount)} = ${money(line.amount)}`}${dayWords ? ` · ${dayWords}` : ''}`,
          tone: line.amount === null ? 'warn' : undefined,
          align: 'right',
          open: () => navigate({ name: 'job', id: task.jobId }),
        }
      }
      const reviewCell: Cell = {
        view: <ReviewWords sheet={sheet} player={player} />,
        detail: reviewDetail(sheet, player),
        action: sheet.sharedAt !== null && player.review === 'disputed'
          ? { label: 'Send back to the player', run: () => resend.mutate({ id: sheet.id, playerId: player.playerId }) }
          : undefined,
      }
      cells.hours = { view: hoursText(hours), detail: `${hoursText(hours)} hours across ${own.length} ${own.length === 1 ? 'job' : 'jobs'}.`, align: 'right' }
      cells.amount = {
        view: (
          <span className={cn('inline-flex items-center gap-1', missing.length > 0 && 'text-status-amber')}>
            {missing.length > 0 ? <Icons.error size="xs" /> : null}
            {money(amount)}
          </span>
        ),
        detail: missing.length > 0
          ? `${money(amount)} — leaves out ${missing.map((line) => sheet.tasks.find((task) => task.jobId === line.jobId)?.jobTitle).join(', ')}, which has no rate.`
          : own.map((line) => `${sheet.tasks.find((task) => task.jobId === line.jobId)?.jobTitle} ${money(line.amount)}`).join(' + ') || '0',
        align: 'right',
      }
      cells.extra = {
        view: player.extraAmount ? money(player.extraAmount) : '',
        detail: player.extraAmount ? `Extra: ${money(player.extraAmount)}` : 'No extra. Type an amount to add one.',
        align: 'right',
        edit: { value: player.extraAmount ? String(player.extraAmount / 100) : '', commit: commitPlayer(player, 'extraAmount'), locked: lockedWords },
      }
      cells.note = {
        view: player.extraReason ?? '',
        detail: player.extraReason ? `Why the extra: ${player.extraReason}` : 'The extra’s reason, which the player reads.',
        edit: { value: player.extraReason ?? '', commit: commitPlayer(player, 'extraReason'), locked: lockedWords },
      }
      cells.total = { view: <span className="font-medium text-foreground">{amountText(total)}</span>, detail: `${money(amount)} + ${money(player.extraAmount) || '0'} extra = ${money(total)}`, align: 'right' }
      cells.method = {
        view: player.method ?? <span className="text-danger">Missing</span>,
        detail: player.method ? `Paid by ${player.method}` : `${player.name} has no payment method on their profile. Type one here.`,
        edit: { value: player.method ?? '', commit: commitPlayer(player, 'method') },
      }
      cells.account = {
        view: <span className="font-mono text-[12px]">{player.account ?? ''}</span>,
        detail: player.account ? `Account: ${player.account}` : 'No account number.',
        edit: { value: player.account ?? '', commit: commitPlayer(player, 'account') },
      }
      cells.review = reviewCell
      cells.status = {
        view: <PayoutMenu player={player} onChange={(next) => setStatus(player, next)} />,
        detail: `${PAYOUT_STATUS_LABEL[player.status]}${player.statusAt ? ` since ${formatCreated(player.statusAt)}` : ''}. Paid records the money in the ledger and marks the work paid.`,
      }
      rows.push({ key: player.playerId, number: index + 3, cells, kind: 'body' })
      csv.push([
        player.name,
        ...tasks.map((task) => {
          const line = lineOf.get(`${player.playerId}:${task.jobId}`)
          return line ? line.hours : ''
        }),
        hours, money(amount), money(player.extraAmount), player.extraReason ?? '', money(total),
        player.method ?? '', player.account ?? '', sheet.sharedAt ? player.review : '', PAYOUT_STATUS_LABEL[player.status],
      ])
    })

    const totalCells: Record<string, Cell> = { player: { view: 'Total', detail: 'Totals for the sheet.', tone: 'strong' } }
    for (const task of tasks) totalCells[`task:${task.jobId}`] = { view: hoursText(taskHours.get(task.jobId) ?? 0), detail: `${hoursText(taskHours.get(task.jobId) ?? 0)} hours on ${task.jobTitle}.`, tone: 'strong', align: 'right' }
    totalCells.hours = { view: hoursText(totals.hours), detail: `${hoursText(totals.hours)} hours in all.`, tone: 'strong', align: 'right' }
    totalCells.amount = { view: money(totals.amount), detail: `Amounts: ${money(totals.amount)}`, tone: 'strong', align: 'right' }
    totalCells.extra = { view: money(totals.extra), detail: `Extras: ${money(totals.extra)}`, tone: 'strong', align: 'right' }
    totalCells.total = { view: money(totals.amount + totals.extra), detail: `To pay in all: ${money(totals.amount + totals.extra)}`, tone: 'strong', align: 'right' }
    for (const key of ['note', 'method', 'account', 'review', 'status']) totalCells[key] = { view: '', detail: '' }
    rows.push({ key: 'total', number: sheet.players.length + 3, cells: totalCells, kind: 'total' })
    csv.push(['Total', ...tasks.map((task) => taskHours.get(task.jobId) ?? 0), totals.hours, money(totals.amount), money(totals.extra), '', money(totals.amount + totals.extra)])
    return { columns, rows, csv }
    // The mutations are stable for the purpose of drawing; the sheet is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheet, clientKey])
}

/** A client's tab: each of their jobs, what the client is charged and what the players earn. */
function clientGrid(sheet: PaymentSheet, clientKey: string): Grid {
  const tasks = sheet.tasks.filter((task) => task.clientKey === clientKey)
  const columns: Column[] = [
    { key: 'job', label: 'Job', width: 240, align: 'left' },
    { key: 'players', label: 'Players', width: 80, align: 'right' },
    { key: 'hours', label: 'Hours', width: 80, align: 'right' },
    { key: 'clientRate', label: 'Client rate', width: 112, align: 'right' },
    { key: 'bill', label: 'Client owes', width: 120, align: 'right' },
    { key: 'playerRate', label: 'Player rate', width: 112, align: 'right' },
    { key: 'earn', label: 'Players earn', width: 120, align: 'right' },
  ]
  const head: Record<string, Cell> = Object.fromEntries(columns.map((column) => [column.key, { view: column.label, detail: String(column.label), tone: 'strong' as const, align: column.align }]))
  const rows: Row[] = [{ key: 'head', number: 1, cells: head, kind: 'head' }]
  const csv: (string | number)[][] = [columns.map((column) => String(column.label))]
  let bill = 0
  let earn = 0
  let hoursAll = 0
  tasks.forEach((task, index) => {
    const lines = sheet.lines.filter((line) => line.jobId === task.jobId)
    const hours = lines.reduce((sum, line) => sum + line.hours, 0)
    const owes = task.clientRateType === null || task.clientRateAmount === null ? null
      : task.clientRateType === 'hourly' ? Math.round(hours * task.clientRateAmount) : task.clientRateAmount
    const earns = lines.reduce((sum, line) => sum + (line.amount ?? 0), 0)
    bill += owes ?? 0
    earn += earns
    hoursAll += hours
    const clientRate = rateWords(task.clientRateType, task.clientRateAmount)
    const playerRate = rateWords(task.rateType, task.rateAmount)
    rows.push({
      key: task.jobId, number: index + 2, kind: 'body',
      cells: {
        job: { view: <span className="font-medium text-foreground">{task.jobTitle}</span>, detail: `${task.jobTitle} — double-click to open the job.`, open: () => navigate({ name: 'job', id: task.jobId }) },
        players: { view: String(lines.length), detail: `${lines.length} ${lines.length === 1 ? 'player' : 'players'} with hours.`, align: 'right' },
        hours: { view: hoursText(hours), detail: `${hoursText(hours)} hours.`, align: 'right' },
        clientRate: { view: clientRate, detail: clientRate ? `The client pays ${clientRate}.` : 'The job has no client rate.', align: 'right', tone: clientRate ? undefined : 'muted' },
        bill: { view: money(owes), detail: owes === null ? 'No client rate, so nothing to bill yet.' : `${hoursText(hours)}h at ${clientRate} = ${money(owes)}`, align: 'right' },
        playerRate: { view: playerRate || 'No rate', detail: playerRate ? `Players earn ${playerRate}.` : 'No player rate yet.', align: 'right', tone: playerRate ? undefined : 'warn' },
        earn: { view: money(earns), detail: `Players earn ${money(earns)} on this job.`, align: 'right' },
      },
    })
    csv.push([task.jobTitle, lines.length, hours, clientRate, money(owes), playerRate, money(earns)])
  })
  rows.push({
    key: 'total', number: tasks.length + 2, kind: 'total',
    cells: {
      job: { view: 'Total', detail: 'Totals for this client.', tone: 'strong' },
      players: { view: '', detail: '' },
      hours: { view: hoursText(hoursAll), detail: `${hoursText(hoursAll)} hours.`, tone: 'strong', align: 'right' },
      clientRate: { view: '', detail: '' },
      bill: { view: money(bill), detail: `The client owes ${money(bill)}.`, tone: 'strong', align: 'right' },
      playerRate: { view: '', detail: '' },
      earn: { view: money(earn), detail: `Players earn ${money(earn)}.`, tone: 'strong', align: 'right' },
    },
  })
  csv.push(['Total', '', hoursAll, '', money(bill), '', money(earn)])
  return { columns, rows, csv }
}
